import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Mail, Instagram, MessageSquare } from "lucide-react";

export default function ContactPage() {
  useSEO({
    title: "Contact Coach Chris — Online Boxing Coach | Better Through Boxing",
    description: "Get in touch with Coach Chris Persaud — questions about virtual boxing lessons, the BTB Basics Program, or general inquiries. Online boxing coaching for beginners and beyond.",
    path: "/contact",
    keywords: ["contact boxing coach", "online boxing coach contact", "Better Through Boxing contact", "Coach Chris Persaud", "boxing coaching inquiry"],
  });

  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/page-config/contact").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const c = cfg || {};
  const hero = c.hero || {}, methods = c.methods || {}, formCfg = c.form || {};

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email required");
    setSubmitting(true);
    try {
      await api.post("/leads/public", { ...form, source: "Contact Page" });
      toast.success("Message received. Coach Chris will be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App relative">
      <SiteHeader />
      <main data-testid="contact-page">
        <section className="border-b border-[#27272A]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-4">{hero.kicker || "Contact"}</div>
            <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] mb-6">{hero.title || "Let's talk."}</h1>
            <p className="text-zinc-300 leading-relaxed max-w-2xl text-base md:text-lg whitespace-pre-line">
              {hero.body || "Questions about online boxing coaching? Wondering if the BTB Basics Program is right for you? Drop a message — Coach Chris reads every one personally."}
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-12 gap-12">
          {/* Contact details */}
          <div className="lg:col-span-4 space-y-6">
            <div>
              <h2 className="font-display uppercase text-2xl tracking-widest mb-4">{methods.heading || "Other ways to reach me"}</h2>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Email</div>
                    <a href={`mailto:${methods.email || "coach@betterthroughboxing.com"}`} className="text-white hover:text-[#FF331F]">{methods.email || "coach@betterthroughboxing.com"}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Instagram className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Instagram (DMs open)</div>
                    <a href={methods.instagram_url || "https://www.instagram.com/flow_state_method/"} target="_blank" rel="noreferrer" className="text-white hover:text-[#FF331F]">{methods.instagram_handle || "@flow_state_method"}</a>
                  </div>
                </li>
              </ul>
            </div>
            <div className="border-l-2 border-[#FF331F] pl-4 text-sm text-zinc-400 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold mb-1">Looking to book?</div>
              <div>Skip the form — <Link to="/book" className="text-[#FF331F] hover:underline">book a session</Link> or <Link to="/services" className="text-[#FF331F] hover:underline">browse services</Link>.</div>
            </div>
            <div className="border-l-2 border-[#FF331F] pl-4 text-sm text-zinc-400 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold mb-1">In New Jersey?</div>
              <div><Link to="/in-person-training" className="text-[#FF331F] hover:underline">Request in-person training</Link> instead.</div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-8 bg-[#0F0F0F] border border-[#27272A] p-8 space-y-4" data-testid="contact-page-form">
            <h3 className="font-display uppercase text-xl tracking-widest flex items-center gap-2 mb-2">
              <MessageSquare className="h-5 w-5 text-[#FF331F]" /> {formCfg.heading || "Send a message"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Name *</label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="contact-page-name" /></div>
              <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Email *</label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="contact-page-email" /></div>
            </div>
            <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Phone (optional)</label><Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="contact-page-phone" /></div>
            <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Message</label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} placeholder={formCfg.placeholder || "Ask anything — programs, scheduling, technique questions, all welcome."} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="contact-page-message" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6" data-testid="contact-page-submit">
              {submitting ? "Sending…" : "Send message"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
